function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { isEmpty } from '@ember/utils';
import { assert } from '@ember/debug';
import EmberObject, { get } from '@ember/object';
import { GetSurveyRequest, Responder } from '@square/dashboard-protos/models/squareup/feedbacksurveys/common';
import { SurveyResponse, CreateResponseRequest } from '@square/dashboard-protos/models/squareup/feedbacksurveys/survey_response';
import { get as getFromLocalStorage, set as setToLocalStorage } from '@square/dashboard-utilities/utils/persistable';
import FeedbackSurveysExternalService from '@dashboard/shared-ui/clients/feedback-survey';
import { OneTrustConsent } from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';
export function isSurveyDismissed(surveyToken, merchantToken) {
  return getFromLocalStorage(`FeedbackSurvey.${surveyToken}.${merchantToken}`);
}
export function setSurveyDismissed(surveyToken, merchantToken) {
  setToLocalStorage(`FeedbackSurvey.${surveyToken}.${merchantToken}`, true, OneTrustConsent.FUNCTIONAL);
}

/**
 * Build a request proto for the GetSurvey endpoint.
 * @param   {String} options.surveyToken token of the requested survey
 * @param   {String} options.locale      language code of the current session
 * @returns {GetSurveyRequest}           Request proto.
 */
function makeGetSurveyRequest({
  surveyToken,
  locale
}) {
  return GetSurveyRequest.create({
    surveyToken,
    locale
  });
}

/**
 * Build a request proto for the CreateResponse endpoint.
 * @param   {String}            options.surveyToken       Token of the survey this response belongs to.
 * @param   {String}            options.merchantToken     Token of the current merchant.
 * @param   {String}            options.employmentToken   Token of the current employee, if any.
 * @param   {String}            options.locale            Langugage code of the current session.
 * @param   {Boolean}           options.dismissed         Defaults to false. Set to true if survey is dismissed by user.
 * @param   {QuestionResponse}  options.response          Proto for response to be recorded. Null if dismissed.
 * @returns {CreateResponseRequest}                       Request proto.
 */
function makeCreateResponseRequest({
  surveyToken,
  merchantToken,
  employmentToken,
  locale,
  surveyResponseToken = null,
  dismissed = false,
  response = null
}) {
  assert('Question must either have a response or be dismissed.', response || dismissed);
  assert('Question cannot be dismissed and have a response.', !(response && dismissed));
  const surveyResponseMetadata = SurveyResponse.SurveyResponseMetadata.create({
    surveyToken,
    locale,
    dismissed,
    surveyResponseToken,
    responder: Responder.create({
      sellerResponderInfo: Responder.SellerResponderInfo.create({
        merchantToken,
        employmentToken
      })
    })
  });
  const questionResponse = response ? [response] : [];
  return CreateResponseRequest.create({
    surveyResponse: SurveyResponse.create({
      surveyResponseMetadata,
      questionResponse
    })
  });
}
export default class Survey extends EmberObject {
  constructor(...args) {
    super(...args);
    // set on load
    _defineProperty(this, "currentSurvey", null);
    _defineProperty(this, "currentQuestion", null);
    _defineProperty(this, "surveyQuestionIndex", 0);
    _defineProperty(this, "surveyLength", null);
    _defineProperty(this, "isCompleted", null);
    _defineProperty(this, "surveyResponseToken", null);
    // passed in on create
    _defineProperty(this, "surveyToken", null);
    _defineProperty(this, "locale", null);
    _defineProperty(this, "merchantToken", null);
    _defineProperty(this, "employmentToken", null);
  }
  init() {
    super.init();
    assert('surveyToken required', this.surveyToken);
  }
  load() {
    if (isSurveyDismissed(this.surveyToken, this.merchantToken)) {
      return Promise.resolve(false);
    } else {
      const getSurveyRequest = makeGetSurveyRequest({
        surveyToken: this.surveyToken,
        locale: this.locale
      });
      return FeedbackSurveysExternalService.getSurvey(getSurveyRequest).then(getSurveyResponse => {
        const currentSurvey = getSurveyResponse.survey;
        if (isEmpty(currentSurvey)) {
          return false;
        } else {
          const currentPage = currentSurvey.page?.[0];
          const currentQuestion = currentPage.surveyQuestion?.[0];
          const surveyLength = currentPage.surveyQuestion.length;
          this.setProperties({
            currentSurvey,
            currentQuestion,
            surveyLength,
            isCompleted: false
          });
          return true;
        }
      });
    }
  }
  setDismissed() {
    setSurveyDismissed(this.surveyToken, this.merchantToken);
    // If there is a surveyResponseToken, a survey has been partially responded to.
    // Don't want to send a dismissed response in this case, or if its been completed.
    if (this.isCompleted || this.surveyResponseToken) {
      return Promise.resolve();
    } else {
      const createResponseRequest = makeCreateResponseRequest({
        dismissed: true,
        locale: this.locale,
        surveyToken: this.surveyToken,
        merchantToken: this.merchantToken,
        employmentToken: this.employmentToken
      });
      return FeedbackSurveysExternalService.createResponse(createResponseRequest);
    }
  }
  incrementSurvey(surveyResponse, options) {
    const surveyQuestionIndex = this.incrementProperty('surveyQuestionIndex');
    if (surveyQuestionIndex >= this.surveyLength) {
      this.set('isCompleted', true);
      if (options.showOnce) {
        setSurveyDismissed(this.surveyToken, this.merchantToken);
      }
    } else {
      this.set('surveyResponseToken', surveyResponse.surveyResponseToken);
      const currentPage = this.currentSurvey.page?.[0];
      const currentQuestion = get(currentPage, `surveyQuestion.${surveyQuestionIndex}`);
      this.set('currentQuestion', currentQuestion);
    }
  }
  setResponseAndIncrementSurvey(response, options = {}) {
    const optionsWithDefaults = Object.assign({
      showOnce: true,
      errorOnFailure: false
    }, options);
    const createResponseRequest = makeCreateResponseRequest({
      response,
      locale: this.locale,
      surveyToken: this.surveyToken,
      merchantToken: this.merchantToken,
      employmentToken: this.employmentToken,
      surveyResponseToken: this.surveyResponseToken
    });
    return FeedbackSurveysExternalService.createResponse(createResponseRequest).then(surveyResponse => {
      this.incrementSurvey(surveyResponse, optionsWithDefaults);
    }).catch(error => {
      this.incrementSurvey({
        surveyResponseToken: null
      }, optionsWithDefaults);
      if (optionsWithDefaults.errorOnFailure) {
        throw error;
      }
    });
  }
}